<template>
  <div class="scheduler-section">
    <h5 class="data-title">message send time</h5>
    <sb-tab :tabs="tabs" :selected.sync="currentTab"/>

    <div class="scheduler-wrapper text-center" :class="[isCampaignScheduled ? 'slide-down' : 'slide-up']">
      <label class="data-description">select date and time</label>
      <sb-date
        :flatpickr="flatpickrConfig"
        :value="laterTime"
        :include="['date', 'time']"
        format="E, MMM d, y h:mm a"
        placeholder="-"
        class="scheduler-time-picker sb-white-input"
        :class="[{'error': !validSendTime}]"
        v-on:update:value="onChangeTime">
      </sb-date>
      <div v-if="!validSendTime" class="error">enter valid date time</div>
      <div v-if="attachedAdHasTimeRange" class="ad-time-range-verbiage">
        <p class="verbiage">this sponsored ad must be sent in between {{timeRange}}</p>
        <div class="divider" />
      </div>
      <label>ensure this timezone is correct</label>
      <span>{{ currentMerchant.timezone }}</span>
      <p>currently {{ currentMerchantTime }}</p>
    </div>
  </div>
</template>

<script>
  import Radios from '../shared/Radios'
  import { getNextAvailableDateTimeUtil, isInDateTimeRangeUtil } from '../../utilities'
  import { format, isAfter } from 'date-fns'
  import { mapGetters, mapState } from 'vuex'
  import { utcToZonedTime } from 'date-fns-tz'

  const laterTab = { label: 'later', key: 'later' },
        nowTab = { label: 'now', key: 'now' }

  export default {
    props: {
      min: Date,
      max: Date,
      sendAtLocal: String,
      attachedAdHasTimeRange: Boolean
    },

    name: 'Scheduler',

    components: {
      Radios,
    },

    data() {
      return {
        calculatedDate: null,
        currentTab: null,
        isScheduled: false,
        laterTime: null,
        noInteractions: true,
        validSendTime: true,
      }
    },

    created() {
      this.laterTime = !!this.sendAtLocal ? new Date(this.sendAtLocal) : null
      this.isScheduled = !!this.laterTime
      this.currentTab = this.isScheduled ? laterTab : nowTab
    },

    computed: {
      flatpickrConfig() {
        return {
          maxDate: this.max,
          minDate: this.minDate,
          altInputClass: 'noCalendar hasTime'
        }
      },

      isCampaignScheduled() {
        return !!this.laterTime || this.isScheduled
      },

      minDate() {
        const now = utcToZonedTime(new Date(), this.currentMerchant.merchant_time_zone),
              adStartTime = this.min,
              adStartsLater = isAfter(adStartTime, now)

        return adStartsLater ? adStartTime : now
      },

      hideSendNow: function() {
        if (!this.min || !this.max) {
          this.currentTab = nowTab
          return false
        }

        const isInRange = isInDateTimeRangeUtil({
          timeZone: this.currentMerchant.merchant_time_zone,
          startHour: this.min.getHours(),
          endHour: this.max.getHours()
        })

        this.calculateDate(isInRange)

        return !isInRange
      },

      tabs() {
        return this.hideSendNow ? [laterTab] : [nowTab, laterTab]
      },

      timeRange() {
        if (!this.min || !this.max) return ""
        return `${format(this.min, 'ha')} - ${format(this.max, 'ha')}`
      },

      ...mapGetters([
        'currentMerchantTime'
      ]),
      ...mapState([
        'currentMerchant'
      ]),
    },

    methods: {
      calculateDate(isInRange) {
        if (this.attachedAdHasTimeRange && !isInRange) {
          this.laterTime = getNextAvailableDateTimeUtil(
            this.currentMerchant.merchant_time_zone,
            this.min,
            this.max
          )
        }

        this.currentTab = isInRange ? nowTab : laterTab
      },

      // Called when date time is selected from sb-date flatpickr
      onChangeTime(selectedDate) {
        const areBeforeAndAfterDatesTheSame = this.laterTime?.toISOString() === selectedDate?.toISOString(),
          isInDateTimeRange = !areBeforeAndAfterDatesTheSame && this.attachedAdHasTimeRange ? isInDateTimeRangeUtil({
                    timeZone: this.currentMerchant.merchant_time_zone,
                    startHour: this.min.getHours(),
                    endHour: this.max.getHours(),
                    chosenDate: selectedDate
                  }) : true
        this.validSendTime = this.isScheduled && selectedDate && (this.attachedAdHasTimeRange ? isInDateTimeRange : true)

        if (this.noInteractions) this.noInteractions = false

        this.laterTime = selectedDate

        if (!!this.laterTime) this.$emit('configUserInteractions', 'schedule', {})

        this.updateCampaignAndValidateScheduler()
      },

      // Called when current tab is changed
      toggleScheduler() {
        let interactions = {}

        this.isScheduled = this.currentTab === laterTab

        if (!this.isScheduled) {
          this.validSendTime = true
          this.laterTime = null
          this.updateCampaignAndValidateScheduler()
        } else {
          interactions = { schedule: true }
          !this.noInteractions && (this.validSendTime = !!this.laterTime)
          this.validateScheduler()
        }

        this.noInteractions && this.$emit('configUserInteractions', 'schedule', interactions)
      },

      // Called to update campaign with send time and whether campaign is set to send now or not
      updateCampaignAndValidateScheduler() {
        if (this.validSendTime) {
          const updateData = { send_at_local: this.isScheduled ? format(this.laterTime, 'y-M-d HH:mm') : null, send_now: !this.isScheduled }
          this.$emit('updateCampaign', updateData)
        }
        this.validateScheduler()
      },

      // Called to tell campaign whether scheduler has an error or not, to block merchant from sending campaign if there are errors
      validateScheduler() {
        const schedulerErrors = this.validSendTime ? {} : { schedulerValid: this.validSendTime }
        this.$emit('isAllValid', 'scheduler', schedulerErrors)
      },
    },

    watch: {
      currentTab: function() {
        this.toggleScheduler()
      }
    }
  }
</script>

<style>
.scheduler-section .sb-tabs .item .label {
  background: unset;
  line-height: 1em;
  padding: .5rem;
}

.scheduler-section .sb-tabs .items-container {
  overflow: hidden;
}

.scheduler-section .sb-tabs .items-container .item {
  overflow: hidden;
}
</style>